import {
  CardTitle,
  DialogDescription,
  DialogTitle,
  Label,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from "@portal/ui";
import { CircleHelp } from "lucide-react";
import type { ReactNode } from "react";

/**
 * 説明のヒント(ユーザー要望 2026-10-15)。ラベルや見出しの右に「?」を置き、マウスを乗せると説明が出る。
 * フォームや機能の説明は画面に書かず、これに入れて文章を減らす。
 * 「?」は Tab のフォーカスの順番に入れない。入れると、ダイアログを開いたときに最初の入力欄ではなく「?」に
 * フォーカスが行き、説明が勝手に開いてしまう。ダイアログの説明は、読み上げ用に別に残す(DialogTitleWithHint)。
 * 空の状態・エラー・状態・消して戻せない操作の注意は、ヒントにせず画面に出す。
 */
export function Hint({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          tabIndex={-1}
          aria-label={label}
          data-slot="hint"
          className="inline-flex size-5 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
        >
          <CircleHelp aria-hidden="true" className="size-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-80 text-left text-pretty">
        {children}
      </TooltipContent>
    </Tooltip>
  );
}

/** 入力欄のラベルと、その説明のヒント。 */
export function LabelWithHint({
  htmlFor,
  children,
  hint,
}: {
  htmlFor?: string;
  children: string;
  hint: ReactNode;
}) {
  return (
    <div className="flex items-center gap-1">
      <Label {...(htmlFor ? { htmlFor } : {})}>{children}</Label>
      <Hint label={`${children}の説明`}>{hint}</Hint>
    </div>
  );
}

/**
 * ダイアログの見出しと、その説明のヒント。説明は読み上げ用にダイアログの説明として残す(画面には出さない)。
 */
export function DialogTitleWithHint({ children, hint }: { children: string; hint: ReactNode }) {
  return (
    <>
      <div className="flex items-center gap-1.5">
        <DialogTitle>{children}</DialogTitle>
        <Hint label={`${children}の説明`}>{hint}</Hint>
      </div>
      <DialogDescription className="sr-only">{hint}</DialogDescription>
    </>
  );
}

/** カードの見出しと、その説明のヒント。 */
export function CardTitleWithHint({
  children,
  hint,
  className,
}: {
  children: string;
  hint: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex items-center gap-1.5", className)}>
      <CardTitle>{children}</CardTitle>
      <Hint label={`${children}の説明`}>{hint}</Hint>
    </div>
  );
}
